import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Badge, EmptyState, IconButton, Page, PageHeader, SearchInput, Segmented, TableSkeleton } from "../../../components/ui";
import { toast } from "react-toastify";
import { getMailEvents, getMailQuota, updateMailQuota } from "../../../services/mailEvents";
import { can } from "../../../utils/roles";
import "./emails.css";
import "../payments-ledger/payments.css";

const EVENTS = {
  accepted: { label: "Accepted", color: "slate" },
  delivered: { label: "Delivered", color: "green" },
  opened: { label: "Opened", color: "blue" },
  clicked: { label: "Clicked", color: "violet" },
  failed: { label: "Failed", color: "red" },
  complained: { label: "Marked as spam", color: "red" },
  unsubscribed: { label: "Unsubscribed", color: "amber" },
};
const when = (date) => (date ? new Date(date).toLocaleString([], { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");

const fmt = (n) => new Intl.NumberFormat().format(n || 0);
const longDay = (date) => new Date(date).toLocaleDateString([], { day: "numeric", month: "long", year: "numeric" });

/** Emails sent this cycle against the plan limit, with the plan settings (limit, renewal day). */
const QuotaCard = () => {
  const { t } = useTranslation();
  const canEdit = can("logs", "edit");
  const [quota, setQuota] = useState(null);
  const [editing, setEditing] = useState(false);
  const [limit, setLimit] = useState("");
  const [cycleDay, setCycleDay] = useState(1);
  const [busy, setBusy] = useState(false);
  const load = (force = false) =>
    getMailQuota(force)
      .then((q) => {
        setQuota(q);
        setLimit(q.limit ? String(q.limit) : "");
        setCycleDay(q.cycleDay || 1);
      })
      .catch(() => setQuota({ error: "The emails could not be loaded." }));
  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      setQuota(await updateMailQuota({ limit: limit ? Number(limit) : null, cycleDay: Number(cycleDay) }));
      setEditing(false);
      toast.success(t("Plan settings saved"));
    } catch (error) {
      toast.error(error?.response?.data?.message || t("Error updating data"));
    } finally {
      setBusy(false);
    }
  };

  if (!quota) return null;
  const tone = quota.percent >= 100 ? "danger" : quota.percent >= 80 ? "warning" : undefined;
  return (
    <section className="ui-card mail-quota-card">
      <div className="mail-quota-head">
        <div>
          <span className="ui-hint">{t("Emails sent this month")}</span>
          <div>
            <strong>{quota.used === null || quota.used === undefined ? "—" : fmt(quota.used)}</strong>
            {quota.limit ? <span className="ui-hint"> / {fmt(quota.limit)}</span> : null}
          </div>
        </div>
        <span className="ui-hint">
          {quota.limit
            ? t("{{remaining}} left · renewed on {{date}}", { remaining: fmt(quota.remaining), date: longDay(quota.cycleEnd) })
            : t("Plan limit not set yet")}
        </span>
      </div>
      {quota.limit && quota.used !== null && (
        <div className="mail-quota-bar" data-tone={tone} role="progressbar" aria-valuenow={Math.min(100, quota.percent)} aria-valuemin={0} aria-valuemax={100} aria-label={t("Emails sent this month")}>
          <span style={{ width: `${Math.min(100, quota.percent)}%` }}></span>
        </div>
      )}
      {quota.error && <p className="ui-hint">{t(quota.error)}</p>}
      <p className="ui-hint">
        {t("Since {{date}} (start of the plan cycle). Mailgun does not give the plan limit through its API: copy it from Mailgun → Plan & Billing.", { date: longDay(quota.cycleStart) })}
      </p>
      {canEdit &&
        (editing ? (
          <div className="mail-quota-settings">
            <label>
              {t("Monthly limit of the plan")}
              <input className="ui-input" type="number" min={100} step={100} value={limit} onChange={(e) => setLimit(e.target.value)} aria-label={t("Monthly limit of the plan")} />
            </label>
            <label>
              {t("Renewal day")}
              <input className="ui-input" type="number" min={1} max={28} value={cycleDay} onChange={(e) => setCycleDay(e.target.value)} aria-label={t("Renewal day")} />
            </label>
            <button type="button" className="ui-btn" disabled={busy} onClick={save}>
              {t("Save")}
            </button>
            <button type="button" className="ui-btn ui-btn-secondary" onClick={() => setEditing(false)}>
              {t("Cancel")}
            </button>
          </div>
        ) : (
          <div>
            <button type="button" className="ui-btn ui-btn-secondary" onClick={() => setEditing(true)}>
              {quota.limit ? t("Change the plan settings") : t("Set the plan limit")}
            </button>
          </div>
        ))}
    </section>
  );
};

/**
 * Emails sent by the platform (Mailgun log): delivered, opened, clicked,
 * failed with the reason. Filter by event, by address and by period.
 */
const Emails = () => {
  const { t } = useTranslation();
  const [event, setEvent] = useState("");
  const [days, setDays] = useState(7);
  const [recipient, setRecipient] = useState("");
  const [items, setItems] = useState(null);
  const [next, setNext] = useState(null);
  const [error, setError] = useState("");
  const [loadingMore, setLoadingMore] = useState(false);

  const load = useCallback(() => {
    setItems(null);
    setError("");
    getMailEvents({ event, days, recipient: recipient.includes("@") ? recipient.trim() : "" })
      .then((data) => {
        setItems(data.items);
        setNext(data.next);
      })
      .catch((failure) => setError(failure?.response?.data?.message || "The emails could not be loaded."));
  }, [event, days, recipient]);
  useEffect(() => {
    const timer = setTimeout(load, recipient ? 500 : 0);
    return () => clearTimeout(timer);
  }, [load, recipient]);

  const more = async () => {
    setLoadingMore(true);
    try {
      const data = await getMailEvents({ page: next });
      setItems((current) => [...current, ...data.items]);
      setNext(data.next);
    } catch (failure) {
      setError(failure?.response?.data?.message || "The emails could not be loaded.");
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <Page>
      <PageHeader
        title={t("Emails sent")}
        subtitle={t("Every email sent by the platform (thanks, reminders, receipts, offers…): delivered, opened, rejected and why.")}
        actions={<IconButton icon="ri-refresh-line" label={t("Refresh")} onClick={load} />}
      />

      <QuotaCard />

      <section className="ui-card">
        <div className="ui-toolbar ledger-toolbar">
          <div className="ui-chips">
            {[["", "All"], ...Object.entries(EVENTS).filter(([key]) => key !== "accepted").map(([key, value]) => [key, value.label])].map(([value, label]) => (
              <button key={value || "all"} type="button" className="ui-chip" aria-pressed={event === value} onClick={() => setEvent(value)}>
                {t(label)}
              </button>
            ))}
          </div>
          <Segmented
            label={t("Period")}
            value={days}
            onChange={setDays}
            options={[
              { value: 1, label: t("24 hours") },
              { value: 3, label: t("3 days") },
              { value: 7, label: t("7 days") },
              { value: 30, label: t("30 days") },
            ]}
          />
          <SearchInput value={recipient} onChange={setRecipient} placeholder={t("Exact email address")} />
        </div>

        {error ? (
          <EmptyState icon="ri-error-warning-line">
            {t(error)}{" "}
            <button type="button" className="ui-btn ui-btn-secondary" onClick={load}>
              {t("Retry")}
            </button>
          </EmptyState>
        ) : !items ? (
          <TableSkeleton />
        ) : !items.length ? (
          <EmptyState icon="ri-mail-line">{t("No email over this period")}</EmptyState>
        ) : (
          <>
            <div className="ui-table-wrap">
              <table className="ui-table">
                <thead>
                  <tr>
                    <th>{t("Date")}</th>
                    <th>{t("Event")}</th>
                    <th>{t("Recipient")}</th>
                    <th>{t("Subject")}</th>
                    <th>{t("Details")}</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => {
                    const known = EVENTS[item.event] || { label: item.event, color: "slate" };
                    return (
                      <tr key={item.id}>
                        <td className="ui-cell-muted">{when(item.at)}</td>
                        <td>
                          <Badge color={known.color} dot>
                            {t(known.label)}
                          </Badge>
                          {item.severity === "temporary" && <span className="ui-media-meta">{t("temporary, Mailgun retries")}</span>}
                        </td>
                        <td>{item.recipient || "—"}</td>
                        <td>{item.subject || "—"}</td>
                        <td className="ui-cell-muted">{item.reason || item.url || item.device || "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {next && (
              <div style={{ padding: "12px 16px" }}>
                <button type="button" className="ui-btn ui-btn-secondary" disabled={loadingMore} onClick={more}>
                  {t("Load more")}
                </button>
              </div>
            )}
          </>
        )}
        <p className="ui-hint" style={{ padding: "0 16px 16px" }}>
          {t("Opens are counted only when the email client loads images; some clients open every email automatically. Mailgun keeps this log for a limited time.")}
        </p>
      </section>
    </Page>
  );
};

export default Emails;
